import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  hackerChecklistService,
  HackerChecklist,
  HackerChecklistInput
} from '../../lib/hackerChecklistsService';
import HackerChecklistEditDialog from './HackerChecklistEditDialog';

const ADMIN_EMAIL = 'admin@rawpa.com';

export const AdminHackerChecklists: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checklists, setChecklists] = useState<HackerChecklist[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<HackerChecklist | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
    });

    const load = async () => {
      try {
        setLoading(true);
        const data = await hackerChecklistService.getChecklists();
        setChecklists(data);
      } catch (err) {
        console.error('Failed to load hacker checklists', err);
        setError('Failed to load hacker checklists. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => unsubscribe();
  }, [auth, navigate]);

  const refreshList = async () => {
    const data = await hackerChecklistService.getChecklists();
    setChecklists(data);
  };

  const handleCreate = () => {
    setEditorTarget(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (checklist: HackerChecklist) => {
    setEditorTarget(checklist);
    setIsEditorOpen(true);
  };

  const handleSave = async (payload: HackerChecklistInput) => {
    if (editorTarget) {
      await hackerChecklistService.updateChecklist(editorTarget.id, payload);
    } else {
      await hackerChecklistService.createChecklist(payload);
    }
    await refreshList();
  };

  const handleDelete = async (checklist: HackerChecklist) => {
    if (!window.confirm(`Delete checklist "${checklist.title}"? This cannot be undone.`)) {
      return;
    }
    try {
      setIsDeleting(checklist.id);
      await hackerChecklistService.deleteChecklist(checklist.id);
      await refreshList();
    } catch (err) {
      console.error('Failed to delete checklist', err);
      alert('Failed to delete checklist. See console for details.');
    } finally {
      setIsDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading hacker checklists…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div
              onClick={() => auth.signOut().then(() => navigate('/aAyuiOcvMq'))}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8 flex justify-between items-start gap-4">
            <div>
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                Hacker Checklists
              </h1>
              <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                Build reusable playbooks for hunters, CTF players, and internal pentests.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/aAyuiOcvMq/dashboard')}>
                Back to Dashboard
              </Button>
              <Button className="bg-[#daed6e] text-black hover:bg-[#c5d85c]" onClick={handleCreate}>
                Create Checklist
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>{error}</p>
            </div>
          )}

          {checklists.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <p>No hacker checklists created yet.</p>
              <p className="text-sm mt-2">Use the button above to create the first one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {checklists.map((checklist) => {
                const sectionsCount = checklist.sections.length;
                const itemsCount = checklist.sections.reduce((total, section) => total + section.items.length, 0);
                const updatedLabel = new Date(checklist.updatedAt).toLocaleString();
                return (
                  <div key={checklist.id} className="border border-[#E7E7E7] rounded-[16px] p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="font-['Space_Grotesk'] font-bold text-lg text-black">
                            {checklist.title}
                          </h2>
                          <span className={`inline-block text-xs px-2 py-1 rounded-full ${checklist.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {checklist.status}
                          </span>
                          {checklist.difficulty && (
                            <span className="inline-block text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                              {checklist.difficulty}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 max-w-3xl">
                          {checklist.description || 'No description provided'}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          <span>Category: {checklist.category || 'general'}</span>
                          <span>Sections: {sectionsCount}</span>
                          <span>Items: {itemsCount}</span>
                          <span>Updated {updatedLabel}</span>
                        </div>
                        {checklist.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 text-xs">
                            {checklist.tags.map((tag) => (
                              <span key={tag} className="px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => handleEdit(checklist)}>
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleDelete(checklist)}
                          disabled={isDeleting === checklist.id}
                        >
                          {isDeleting === checklist.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <HackerChecklistEditDialog
        open={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        onSave={handleSave}
        initialData={editorTarget || undefined}
        isNew={!editorTarget}
      />
    </div>
  );
};

export default AdminHackerChecklists;

